// src/screens/TwelveMonthPlan.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Phase 7 — Twelve-month plan. Monthly April→March grid showing
// all standing orders and key milestones. Accessible from Tools sidebar.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

function getMonthIndex(monthStr: string): number {
  const m = monthStr.substring(0, 3);
  return MONTHS.indexOf(m);
}

interface MonthRow {
  month: string;
  year: number;
  items: { label: string; amount: number; isNew?: boolean; ends?: boolean }[];
  total: number;
}

function buildTwelveMonths(data: ClientData): MonthRow[] {
  // Determine start year (current tax year)
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; // Apr start

  const rows: MonthRow[] = [];

  for (let i = 0; i < 12; i++) {
    const monthLabel = MONTHS[i];
    const calendarYear = i < 9 ? startYear : startYear + 1; // Apr-Dec = startYear, Jan-Mar = startYear+1
    const items: { label: string; amount: number; isNew?: boolean; ends?: boolean }[] = [];

    for (const so of data.standingOrders) {
      // Check if this SO is active in this month
      let active = true;

      // Parse start date
      if (so.startDate && so.startDate !== 'Immediate' && so.startDate !== 'Next month') {
        const startIdx = getMonthIndex(so.startDate);
        if (startIdx > i && so.startDate.includes(startYear.toString())) {
          active = false;
        }
      }

      // Parse end date
      if (so.endDate) {
        const endIdx = getMonthIndex(so.endDate);
        if (endIdx >= 0 && endIdx < i) {
          active = false;
        }
      }

      if (active) {
        items.push({
          label: so.label,
          amount: so.amount,
          isNew: i === 0 || (so.startDate !== 'Immediate' && getMonthIndex(so.startDate) === i),
          ends: so.endDate ? getMonthIndex(so.endDate) === i : false,
        });
      }
    }

    const total = items.reduce((sum, item) => sum + item.amount, 0);
    rows.push({ month: monthLabel, year: calendarYear, items, total });
  }

  return rows;
}

export default function TwelveMonthPlan({ data }: Props) {
  const rows = buildTwelveMonths(data);
  const annualTotal = rows.reduce((sum, r) => sum + r.total, 0);

  // Unique SO labels for the header
  const allLabels = Array.from(new Set(data.standingOrders.map(so => so.label)));

  if (data.standingOrders.length === 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-center">
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-gray-600 mb-2">No standing orders yet</h2>
          <p className="text-sm text-gray-400">Complete the session and set up standing orders in M6 first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
        <h2 className="text-lg font-bold text-ma-navy mb-1">12-Month Plan</h2>
        <p className="text-sm text-blue-600">
          Monthly breakdown of all standing orders, April to March. Annual total: {fmt(annualTotal)}.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide sticky left-0 bg-gray-50">Month</th>
              {allLabels.map(label => (
                <th key={label} className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                  {label.length > 18 ? label.substring(0, 16) + '...' : label}
                </th>
              ))}
              <th className="text-right px-3 py-2.5 text-xs font-bold text-ma-navy uppercase tracking-wide">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className={`border-b border-gray-100 ${idx % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                <td className="px-3 py-2.5 font-medium text-gray-700 sticky left-0 bg-white">
                  {row.month} {row.year.toString().slice(-2)}
                </td>
                {allLabels.map(label => {
                  const item = row.items.find(i => i.label === label);
                  return (
                    <td key={label} className={`text-right px-3 py-2.5 ${
                      item ? 'text-ma-navy font-medium' : 'text-gray-200'
                    }`}>
                      {item ? fmt(item.amount) : '—'}
                    </td>
                  );
                })}
                <td className="text-right px-3 py-2.5 font-bold text-ma-navy">{fmt(row.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-ma-navy/5 border-t-2 border-ma-navy/20">
              <td className="px-3 py-3 font-bold text-ma-navy sticky left-0 bg-ma-navy/5">Annual</td>
              {allLabels.map(label => {
                const colTotal = rows.reduce((sum, row) => {
                  const item = row.items.find(i => i.label === label);
                  return sum + (item ? item.amount : 0);
                }, 0);
                return (
                  <td key={label} className="text-right px-3 py-3 font-bold text-ma-navy">{fmt(colTotal)}</td>
                );
              })}
              <td className="text-right px-3 py-3 font-bold text-lg text-ma-navy">{fmt(annualTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
